import * as errors from '../errors'

/**
 * Contains {@link UserFriendlyErrorProducer} to use in
 * {@link makeUserFriendly}
 */
const plugins: makeUserFriendly.Plugin[] = []

/**
 * Converts an {@link Error} (or any other value) into a
 * {@link errors.UserFriendlyError}, if it is not one already
 */
export function makeUserFriendly(error: unknown): errors.UserFriendlyError {
  while (
    (error instanceof AggregateError ||
      error instanceof errors.UserFriendlyAggregateError) &&
    error.errors.length === 1
  ) {
    // In cases of aggregate errors with one entry, we unpack the aggregate
    // error since it is not very useful. This is done in a while loop for the
    // unlikely case of nested aggregate errors
    error = error.errors[0] satisfies Error as Error
  }
  if (error instanceof errors.UserFriendlyError) {
    // If the error is already user-friendly, we can just return it
    return error
  }
  if (error instanceof AggregateError) {
    // If it is an aggregate error, we just turn it into a user-friendly
    // aggregate error
    return new errors.UserFriendlyAggregateError(error)
  }
  // We attempt to use plugins to handle other types of errors
  for (const plugin of plugins) {
    const userFriendlyError = plugin(error)
    if (userFriendlyError) {
      return userFriendlyError
    }
  }
  // If no plugins apply, then we just throw an unexpected error
  return new errors.UnexpectedError(error instanceof Error ? error : undefined)
}
export namespace makeUserFriendly {
  /**
   * Registers a {@link Plugin} for {@link makeUserFriendly}
   */
  export function registerPlugin(plugin: Plugin): void {
    plugins.push(plugin)
  }

  /**
   * When registered through {@link makeUserFriendly.registerPlugin}, this is
   * used by {@link makeUserFriendly} to create a
   * {@link errors.UserFriendlyError} in special cases
   *
   * Returning `undefined` allows {@link makeUserFriendly} to use another
   * {@link Plugin} or its default logic
   *
   * @example
   * errors.makeUserFriendly.registerPlugin(
   *   error =>
   *     error instanceof externalLibrary.SpecialError
   *       ? new errors.ValidationError(...)
   *       : undefined
   * )
   */
  export type Plugin = (error: unknown) => errors.UserFriendlyError | undefined
}
